// src/pages/components/otp-popup.ts
import './otp-popup.css';
import { requestApi } from '../../core/api-client';

interface OtpPopupOptions {
  otpId: string;
  onSuccess: (data: any) => void;
}

export const OtpPopup = {
  currentOptions: null as OtpPopupOptions | null,
  isLoaded: false,

  // Hàm tự động tải HTML từ file template và chèn vào DOM
  async ensureHtmlLoaded(): Promise<boolean> {
    if (this.isLoaded && document.getElementById('otpSharedModal')) {
      return true;
    }

    try {
      // Fetch file HTML từ thư mục components
      const response = await fetch('/src/pages/components/otp-popup.html');
      if (!response.ok) throw new Error('Không thể tải file template OTP.');
      
      const htmlText = await response.text();

      // Tạo container tạm để nhúng HTML vào cuối body
      const container = document.createElement('div');
      container.innerHTML = htmlText;
      document.body.appendChild(container);

      this.isLoaded = true;
      this.bindEvents(); // Gắn các sự kiện form, input sau khi HTML đã sẵn sàng
      return true;
    } catch (error) {
      console.error('Lỗi load otp-popup.html:', error);
      return false;
    }
  },

  bindEvents() {
    const modal = document.getElementById('otpSharedModal');
    const form = document.getElementById('otpSharedForm') as HTMLFormElement;
    const inputs = document.querySelectorAll('.otp-shared-input') as NodeListOf<HTMLInputElement>;
    const btnClose = document.getElementById('btnCloseOtpShared');
    const errorBox = document.getElementById('otpSharedError');
    const successBox = document.getElementById('otpSharedSuccess');

    if (!modal || !form) return;

    // Xử lý đóng Modal
    const closeModal = () => {
      modal.classList.remove('active');
      modal.style.display = 'none';
    };

    btnClose?.addEventListener('click', closeModal);

    // Click ra ngoài modal card để đóng
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal();
      }
    });

    // Xử lý nhập liệu OTP (nhảy ô)
    inputs.forEach((input, index) => {
      input.addEventListener('input', (e: Event) => {
        const target = e.target as HTMLInputElement;
        target.value = target.value.replace(/[^0-9]/g, '').slice(-1);
        if (target.value && index < inputs.length - 1) inputs[index + 1].focus();
      });

      input.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Backspace' && !input.value && index > 0) {
          inputs[index - 1].focus();
          inputs[index - 1].value = '';
        }
      });
      
      input.addEventListener('paste', (e: ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData?.getData('text')?.replace(/[^0-9]/g, '') || '';
        const digits = text.split('');
        let curIdx = index;
        digits.forEach(digit => {
          if (curIdx < inputs.length) {
            inputs[curIdx].value = digit;
            curIdx++;
          }
        });
        inputs[Math.min(curIdx, inputs.length - 1)]?.focus();
      });
    });

    // Xử lý Submit Verify
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const code = Array.from(inputs).map(i => i.value).join('');
      if (code.length < 6) {
        if (errorBox) { errorBox.style.display = 'block'; errorBox.textContent = 'Vui lòng nhập đủ 6 số.'; }
        return;
      }

      const btnSubmit = document.getElementById('btnSubmitOtpShared') as HTMLButtonElement;
      const originalText = btnSubmit.innerHTML;
      btnSubmit.disabled = true;
      btnSubmit.innerHTML = `<i class="fas fa-spinner fa-spin"></i> Đang xác thực...`;
      
      if (errorBox) errorBox.style.display = 'none';
      if (successBox) successBox.style.display = 'none';

      try {
        const payload = {
          IdOtp: this.currentOptions?.otpId,
          code: code
        };

        const result: any = await requestApi('/api/app/otp/verify', 'POST', payload);
        const resData = result?.result || result;

        if (!resData?.isVerified) throw new Error('Mã OTP không chính xác hoặc đã hết hạn.');

        if (successBox) {
          successBox.style.display = 'block';
          successBox.textContent = 'Xác thực thành công!';
        }

        // Đợi 1 giây rồi gọi Callback Success và đóng form
        setTimeout(() => {
          closeModal();
          if (this.currentOptions?.onSuccess) {
            this.currentOptions.onSuccess(resData);
          }
        }, 1000);

      } catch (error: any) {
        if (errorBox) {
          errorBox.style.display = 'block';
          errorBox.textContent = error.message || 'Xác thực thất bại.';
        }
      } finally {
        btnSubmit.disabled = false;
        btnSubmit.innerHTML = originalText;
      }
    });
  },

  async show(options: OtpPopupOptions) {
    this.currentOptions = options;

    // Đảm bảo HTML đã được tải và chèn vào DOM trước khi show
    const loaded = await this.ensureHtmlLoaded();
    if (!loaded) return;

    const modal = document.getElementById('otpSharedModal');
    const inputs = document.querySelectorAll('.otp-shared-input') as NodeListOf<HTMLInputElement>;
    const errorBox = document.getElementById('otpSharedError');
    const successBox = document.getElementById('otpSharedSuccess');

    // Reset Form
    inputs.forEach(i => i.value = '');
    if (errorBox) errorBox.style.display = 'none';
    if (successBox) successBox.style.display = 'none';

    if (modal) {
      // Ép hiển thị bằng cả class 'active' và inline style !important để chống bị CSS chung đè
      modal.classList.add('active');
      modal.style.cssText = 'display: flex !important; z-index: 2050 !important;';
      
      inputs[0]?.focus();
    }
  }
};